import React, { lazy, Suspense, useState } from 'react';

import { OfflineStudentViewModes, OnlineStudentViewModes } from '../../../../config/constants';
import { StartupOptions } from '../../../../config/startupOptions';
import { throwOnUnset } from '../../../../global/utils/throwOnUnset';
import { setGlobalPanorama } from '../../../globalPanoramaAccessor';
import { ProjectNotAvailableErrorOverlay } from './Loader/ErrorOverlays/ProjectNotAvailableErrorOverlay';
import { TrackingNotInitializedErrorOverlay } from './Loader/ErrorOverlays/TrackingNotInitializedErrorOverlay';
import { UnknownLoadingErrorOverlay } from './Loader/ErrorOverlays/UnknownLoadingErrorOverlay';
import { AnimatedLoader } from './Loader/Loader';
import {
  useApplicationPreInitQuery,
  useOfflineStartupQuery,
  useOnlineStudentStartupQuery,
} from './startup/startupHooks';
import type { ProjectVM } from '@ael/core-api';
import { IPanorama } from '@ael/core-api';
import { Helmet } from 'react-helmet';

const StudentAppComponent = lazy(() => import('./StudentApp').then((module) => ({ default: module.StudentApp })));

function handleGlobalPanoramaReady(onHandled: () => void, panorama: IPanorama) {
  setGlobalPanorama(panorama);
  onHandled();
}

type OfflineAppBuilderProps = {
  options: StartupOptions<OfflineStudentViewModes>;
};

export const OfflineAppBuilder = ({ options }: OfflineAppBuilderProps) => {
  const [projectViewModel] = useState(() =>
    throwOnUnset(
      // window.courseConfig is only set in offline/exported builds despite the non-optional typing
      window.courseConfig as ProjectVM | undefined,
      'window.courseConfig not set, cannot run an offline course without it.'
    )
  );
  const branding = {
    tagline: projectViewModel.settings.tagLine,
    logoUrl: window.logoUrl,
  };
  const hideLoader = options.hideLoader || projectViewModel.additionalSettings.hideLoader === true;
  const { data } = useOfflineStartupQuery(options.viewMode, projectViewModel);
  const [isAppReady, setIsAppReady] = useState(false);

  if (data && !data.trackingResult.isSuccess) {
    return <TrackingNotInitializedErrorOverlay />;
  }

  // Read from the config directly, not from the query result: the loader has to be on screen while
  // tracking initializes, which is a live LRS round-trip for the TinCan modes.
  const isLightModeEnabled = projectViewModel.config.theme.isLightThemeEnabled;
  return (
    <>
      <Helmet>
        <html className={isLightModeEnabled ? 'light' : 'dark'} />
      </Helmet>
      {!hideLoader && (
        <AnimatedLoader isAppReady={isAppReady} branding={branding} isLightModeEnabled={isLightModeEnabled} />
      )}
      {data?.trackingResult.isSuccess === true && (
        <Suspense>
          <StudentAppComponent
            options={options}
            onStudentReady={(panorama) => handleGlobalPanoramaReady(() => setIsAppReady(true), panorama)}
            projectViewModel={projectViewModel}
            persistenceTrackingClient={data.trackingResult.trackingClient}
            persistedTrackingData={data.trackingResult.persistedTrackingData}
          />
        </Suspense>
      )}
    </>
  );
};

type AppBuilderProps = {
  options: StartupOptions<OnlineStudentViewModes>;
};

export const AppBuilder = ({ options }: AppBuilderProps) => {
  const { data } = useApplicationPreInitQuery(options.viewMode);
  const { data: startupData } = useOnlineStudentStartupQuery(data, options.viewMode);

  const [isAppReady, setIsAppReady] = useState(false);

  if (!data) {
    return null;
  }

  if (data.preInitData === null || data.preInitData === 'not_found') {
    if (data.preInitData === 'not_found') {
      return <ProjectNotAvailableErrorOverlay />;
    } else {
      return <UnknownLoadingErrorOverlay />;
    }
  }

  if (startupData && startupData.trackingResult && !startupData.trackingResult.isSuccess) {
    return <TrackingNotInitializedErrorOverlay />;
  }

  const isLightModeEnabled = data.preInitData.theme.isLightThemeEnabled;

  return (
    <>
      <Helmet>
        <html className={isLightModeEnabled ? 'light' : 'dark'} />
      </Helmet>
      {!options.hideLoader && (
        <AnimatedLoader
          isAppReady={isAppReady}
          branding={data.preInitData.branding}
          isLightModeEnabled={isLightModeEnabled}
        />
      )}
      {startupData?.trackingResult?.isSuccess === true && (
        <Suspense>
          <StudentAppComponent
            options={options}
            onStudentReady={(panorama) => handleGlobalPanoramaReady(() => setIsAppReady(true), panorama)}
            projectViewModel={startupData.projectViewModel}
            persistenceTrackingClient={startupData.trackingResult.trackingClient}
            persistedTrackingData={startupData.trackingResult.persistedTrackingData}
          />
        </Suspense>
      )}
    </>
  );
};
